/**
 * A3 (2026-06-27) — Customer Complete Profile (web).
 *
 * Mandatory post-registration step. Fields: Full Name, Phone, DOB.
 *
 * Hard rules per operator spec:
 *   - NO Skip / Later / Close.
 *   - Prevent navigation away while `profile_completed=false` — the
 *     browser back button is intercepted via a `popstate` listener
 *     that re-pushes the current URL.
 *   - On submit success → refresh /api/auth/me → navigate to
 *     /customer/home.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Calendar } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';

const MIN_AGE_YEARS = 13;

function todayMinusYears(years) {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return d;
}

function isoDate(d) {
  return [
    d.getFullYear().toString().padStart(4, '0'),
    (d.getMonth() + 1).toString().padStart(2, '0'),
    d.getDate().toString().padStart(2, '0'),
  ].join('-');
}

export default function CustomerCompleteProfilePage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const signOut = useAuthStore((s) => s.signOut);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');  // yyyy-mm-dd from <input type="date">
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const maxDate = useMemo(() => isoDate(todayMinusYears(MIN_AGE_YEARS)), []);
  const minDate = useMemo(() => isoDate(todayMinusYears(100)), []);

  // Prefill from the authenticated user, except the placeholder "New Member".
  useEffect(() => {
    if (!user) return;
    if (user.name && user.name !== 'New Member') {
      setFullName((prev) => prev || user.name);
    }
    if (user.phone) setPhone((prev) => prev || user.phone);
    if (user.date_of_birth) setDob((prev) => prev || user.date_of_birth);
  }, [user]);

  // Block browser back / "Close tab" while profile is incomplete.
  useEffect(() => {
    if (user?.profile_completed) return undefined;
    // Push a sentinel history entry so popstate fires on any Back press.
    window.history.pushState({ gated: 'complete-profile' }, '', window.location.href);
    const onPop = () => {
      // Immediately re-push to defeat the back navigation.
      window.history.pushState({ gated: 'complete-profile' }, '', window.location.href);
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, [user]);

  // No token at all? Nothing we can submit — bounce to login.
  useEffect(() => {
    if (!token) {
      navigate('/customer/login', { replace: true });
    }
  }, [token, navigate]);

  const canSubmit =
    !submitting &&
    fullName.trim().length >= 2 &&
    phone.replace(/[^\d]/g, '').length >= 7 &&
    Boolean(dob) &&
    dob <= maxDate &&
    dob >= minDate;

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setErr(null);
      if (!canSubmit) {
        if (fullName.trim().length < 2) {
          setErr({ title: 'Full name required', message: 'Please enter your full name.' });
        } else if (phone.replace(/[^\d]/g, '').length < 7) {
          setErr({ title: 'Phone required', message: 'Please enter a valid phone number.' });
        } else if (!dob) {
          setErr({ title: 'Date of birth required', message: 'Please pick your date of birth.' });
        } else if (dob > maxDate) {
          setErr({ title: 'Too young', message: `You must be at least ${MIN_AGE_YEARS} years old.` });
        }
        return;
      }
      setSubmitting(true);
      try {
        await api.completeProfile(token, {
          full_name:     fullName.trim(),
          phone:         phone.trim(),
          date_of_birth: dob,
        });
        await refreshUser();
        navigate('/customer/home', { replace: true });
      } catch (e2) {
        setErr({
          title: 'Could not save profile',
          message: e2?.detail || 'Please try again.',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [canSubmit, fullName, phone, dob, maxDate, token, refreshUser, navigate]
  );

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center px-4 py-10"
      data-testid="customer-complete-profile-page"
    >
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <img src="/onestamp-logo.png" alt="OneStamp" className="w-12 h-12 rounded-2xl" />
          <div className="text-xs text-gold uppercase tracking-widest font-semibold">
            OneStamp
          </div>
        </div>

        <div className="rounded-3xl bg-[#15151B] border border-white/8 p-7 shadow-2xl">
          <h1 className="font-display text-2xl font-semibold mb-2">
            One quick step
          </h1>
          <p className="text-sm text-white/55 mb-6">
            We'll use these details across every brand you join.
          </p>

          {err ? (
            <div
              className="mb-5 px-3 py-2.5 rounded-xl bg-rose-500/8 border border-rose-400/25 flex items-start gap-2 text-xs"
              data-testid="customer-complete-profile-error"
              role="alert"
            >
              <AlertCircle size={14} className="text-rose-300 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-rose-200">{err.title}</div>
                <div className="text-rose-200/80 mt-0.5">{err.message}</div>
              </div>
            </div>
          ) : null}

          <form
            onSubmit={onSubmit}
            className="space-y-4"
            data-testid="customer-complete-profile-form"
            noValidate
          >
            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider">
                Full name
              </label>
              <input
                type="text"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                data-testid="customer-complete-profile-name"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors"
                placeholder="Andre Wijaya"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider">
                Phone number
              </label>
              <input
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="customer-complete-profile-phone"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors"
                placeholder="+62 812 …"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider flex items-center gap-1">
                <Calendar size={11} /> Date of birth
              </label>
              <input
                type="date"
                value={dob}
                max={maxDate}
                min={minDate}
                onChange={(e) => setDob(e.target.value)}
                data-testid="customer-complete-profile-dob"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors text-white"
              />
              <div className="mt-1 text-[11px] text-white/35">
                You must be at least {MIN_AGE_YEARS} years old.
              </div>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="customer-complete-profile-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-xl py-2.5 text-sm hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-glow-gold-sm"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Saving…
                </>
              ) : (
                'Save and continue'
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-[11px] text-white/35">
            {/* Explicit opt-out of the account via Sign out — this is
                NOT a "Skip" of the profile gate; it is a logout.
                Operator spec allows the user to abandon the flow by
                signing out, not by bypassing it. */}
            <button
              type="button"
              onClick={() => {
                signOut();
                navigate('/customer/login', { replace: true });
              }}
              data-testid="customer-complete-profile-signout"
              className="hover:text-white/70 transition-colors"
            >
              Not now — sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
